import { Component, EventEmitter, Input, OnChanges, OnInit, Output, SimpleChanges } from "@angular/core";
import { FormControl, FormGroup, Validators } from "@angular/forms";
import { CustomFormValidators } from "@app/directives/_forms/form-validation.service";
import { IPhoneNumberOutput } from "@app/directives/_forms/smsInput/smsInput.interfaces";
import { IVTableColumn } from "@app/directives/_layout/v-table/_models/IVTableColumn";
import { IVTableRow } from "@app/directives/_layout/v-table/_models/IVTableRow";
import { VTableService } from "@app/directives/_layout/v-table/_services/v-table.service";
import { VPillsComponent } from "@app/directives/_veri/v-pills/v-pills.component";
import { SortDirection } from "@app/enum/SortDirection";
import { IUser } from "@app/interfaces/IUser";
import { IReseller } from "@app/main/modules/foundation/_models/IReseller";
import { IUserGroup } from "@app/main/modules/foundation/_models/IUserGroup";
import { UserGroupService } from "@app/main/modules/foundation/_services/user-group.service";
import { ResellerUserGroupMappingService } from "@app/services/resellerUserGroupMapping.service";
import { UntilDestroy, untilDestroyed } from "@ngneat/until-destroy";
import { combineLatest, map, mergeMap, tap } from "rxjs";
import './engineer-company.component.scss';

@UntilDestroy()
@Component({
    selector: "engineer-company",
    template: require("./engineer-company.component.html")
})
export class EngineerCompanyComponent implements OnInit, OnChanges {
    @Input() userGroups: IUserGroup[];
    @Input() resellers: IReseller[];
    @Input() engineers: IUser[];
    updatedMappedResellerIds: number[];

    @Output() companyUpdated: EventEmitter<IUserGroup> = new EventEmitter<IUserGroup>();

    selectedUserGroup: IUserGroup;

    userGroupEngineerCounts: Record<number, number>;

    tableColumns: IVTableColumn<IUserGroup>[];
    tableRows: IVTableRow[];

    updatedPhoneNumber: IPhoneNumberOutput;

    companyEditForm: FormGroup;

    isNewUserGroup: boolean = false;

    constructor(
        private readonly vTableService: VTableService,
        private readonly userGroupService: UserGroupService,
        private readonly resellerUserGroupService: ResellerUserGroupMappingService,
    ) { }

    ngOnInit(): void {
        this.createTable();
    }

    ngOnChanges(changes: SimpleChanges): void {
        this.userGroupEngineerCounts = this.engineers?.reduce<Record<number, number>>((acc, user) => {
            acc[user.UserGroupId] = (acc[user.UserGroupId] || 0) + 1;
            return acc;
        }, {});

        this.tableRows = this.vTableService.createTableRows(this.userGroups, this.tableColumns);
    }

    onResellerChange($event: any): void {
        this.updatedMappedResellerIds = $event;
        this.companyEditForm.get("ResellerMappings").setValue($event);
    }

    rowClicked($event: IVTableRow): void {
        this.selectedUserGroup = $event.data;

        // switch to Michal's deepCopy method once in master
        this.updatedMappedResellerIds = JSON.parse(JSON.stringify(this.selectedUserGroup.mappedResellerIds)) as number[];

        this.createForm();
    }

    closeUserGroupSidebar(): void {
        this.selectedUserGroup = null;
        this.isNewUserGroup = false;
    }

    onPhoneNumberChanged($event: IPhoneNumberOutput): void {
        this.updatedPhoneNumber = $event;
    }

    isUserGroupFormValid(): boolean {
        return false;
    }

    addUserGroup(): void {
        this.isNewUserGroup = true;
        this.selectedUserGroup = {
            Id: 0,
            Description: "",
            Email: "",
            Phone: "",
            mappedResellerIds: []
        }

        this.updatedMappedResellerIds = this.selectedUserGroup.mappedResellerIds;

        this.createForm();
    }

    saveUserGroup(): void {
        this.selectedUserGroup.mappedResellerIds = this.updatedMappedResellerIds;

        if (this.updatedPhoneNumber.isValid) {
            this.selectedUserGroup.Phone = this.updatedPhoneNumber.internationalized
        }

        this.userGroupService.saveUserGroup(this.selectedUserGroup).pipe(
            untilDestroyed(this),
            mergeMap(userGroup => {
                this.selectedUserGroup = userGroup;
                return this.resellerUserGroupService.updateMappings(this.updatedMappedResellerIds, userGroup.Id).pipe(
                    map(resellerMappings => ({ userGroup, resellerMappings }))
                );
            }),
            tap(({ userGroup, resellerMappings }) => {
                userGroup.mappedResellerIds = resellerMappings.filter(m => m.EndTime == null).map(r => r.ResellerId);
                userGroup.resellerPills = userGroup.mappedResellerIds.map(resellerId => {
                    const reseller = this.resellers.find(r => r.Id === resellerId);
                    return {
                        label: reseller?.Description,
                        hovering: false,
                        data: reseller,
                        tooltip: reseller?.Description
                    };
                });

                const index = this.userGroups.findIndex(group => group.Id === userGroup.Id);
                if (index !== -1) {
                    this.userGroups[index] = userGroup;
                } else {
                    this.userGroups.push(userGroup);
                }

                this.companyUpdated.emit(userGroup);
                this.tableRows = this.vTableService.createTableRows(this.userGroups, this.tableColumns);

                this.selectedUserGroup = null;
                this.updatedMappedResellerIds = null;
            })
        ).subscribe();
    }

    private createForm(): void {

        this.companyEditForm = new FormGroup({
            CompanyName: new FormControl(this.selectedUserGroup.Description, [
                Validators.required,
                CustomFormValidators.emptyWhiteSpace,
                CustomFormValidators.duplicateProperty(this.userGroups.filter(x => x.Id !== this.selectedUserGroup?.Id), "Description", "engineerCompanyNameDuplicated")]),
            ResellerMappings: new FormControl(this.updatedMappedResellerIds, [CustomFormValidators.minimumArrayLength(1)])
        })
    }

    private createTable(): void {
        this.tableColumns = [
            {
                header: this.vTableService.createTableHeader({ tKey: "EngineerUsers.CompanyName", sorted: true, sortDirection: SortDirection.Descending }),
                createCellFunction: (company) => this.vTableService.createTableCell(company.Description)
            },
            {
                header: this.vTableService.createTableHeader({ tKey: "Common.Reseller" }),
                createCellFunction: (company) => this.vTableService.createTableCell(company, {
                    component: VPillsComponent,
                    componentInputs: {
                        pills: company.resellerPills
                    },
                    exportData: company.resellerPillsExportData
                })
            },
            {
                header: this.vTableService.createTableHeader({ tKey: "EngineerUsers.EngineerCount" }),
                createCellFunction: (company) => this.vTableService.createTableCell(this.userGroupEngineerCounts[company.Id])
            }
        ]
    }
}
